/**
 * 卡片上的「快速記一筆進度」（spec 13.5）。
 *
 * 進度紀錄直接寫進 `description`，不新增資料表：這樣備份、匯入、搜尋與詳細抽屜
 * 都不必改，使用者也永遠看得到、改得動自己打的字。格式是每行一筆：
 *
 *     [08/24 14:30] 已寄信給廠商
 */

export interface ProgressEntry {
  /** MM/DD HH:mm 的本地牆上時間。 */
  stamp: string;
  text: string;
}

const STAMP_PATTERN = /^\[(\d{2}\/\d{2} \d{2}:\d{2})\]\s*(.*)$/;

function pad2(value: number): string {
  return value.toString().padStart(2, '0');
}

/** 進度時間戳記用本地時間，使用者看到的才是自己記錄當下的鐘面時間。 */
export function formatProgressStamp(at: Date = new Date()): string {
  return `${pad2(at.getMonth() + 1)}/${pad2(at.getDate())} ${pad2(at.getHours())}:${pad2(at.getMinutes())}`;
}

/** 把一筆進度接在說明後面；原本的說明一字不動地保留。 */
export function appendProgressEntry(
  description: string | null,
  note: string,
  at: Date = new Date(),
): string {
  const trimmed = note.trim();
  if (!trimmed) return description ?? '';

  const entry = `[${formatProgressStamp(at)}] ${trimmed}`;
  const base = (description ?? '').replace(/\s+$/, '');
  return base ? `${base}\n${entry}` : entry;
}

/** 最後一筆有時間戳記的進度；沒有就回 null。 */
export function latestProgressEntry(description: string | null): ProgressEntry | null {
  const lines = (description ?? '').split('\n');

  for (let index = lines.length - 1; index >= 0; index -= 1) {
    const match = STAMP_PATTERN.exec(lines[index] ?? '');
    if (!match) continue;

    // 進度後面沒有戳記的行視為同一筆的續行，一起顯示。
    const continuation = lines
      .slice(index + 1)
      .map((line) => line.trim())
      .filter(Boolean);

    return {
      stamp: match[1] ?? '',
      text: [match[2]?.trim() ?? '', ...continuation].filter(Boolean).join(' '),
    };
  }

  return null;
}

/** 還沒記過進度時，卡片改摘要說明的第一行。 */
export function descriptionExcerpt(description: string | null): string | null {
  const line = (description ?? '')
    .split('\n')
    .map((current) => current.trim())
    .find(Boolean);
  return line ?? null;
}

/** 卡片正面要顯示的那一行。 */
export function cardProgressSummary(
  description: string | null,
): { stamp: string | null; text: string } | null {
  const entry = latestProgressEntry(description);
  if (entry && entry.text) return { stamp: entry.stamp, text: entry.text };

  const excerpt = descriptionExcerpt(description);
  return excerpt ? { stamp: null, text: excerpt } : null;
}
